/**
 * shared/export.ts —— 快报导出：**长图（PNG）** 与 **单页 PDF**
 * 出处：01_需求文档/16_项目快报方案.md §七
 *
 * ## 🔴 为什么不是「浏览器打印成 A4」
 * `02` §5.6 原来写的是打印。**深色满版底走不了浏览器打印**：背景默认被剥掉
 * （打出来白底黑字、主视觉全没），要用户手动勾「背景图形」才印得出，而且极费墨。
 * 用户明确的场景本来就是**截图 / 发 PDF**，不是打印上纸。
 *
 * ## 为什么不引 html2canvas
 * 快报的主视觉大量用 `background-clip:text` 的渐变字、`skewX` 斜切、多层 `radial-gradient`。
 * html2canvas 是**自己实现的一套渲染器**，`background-clip:text` 它不支持——
 * 标题会直接变成一块纯色，而这恰恰是这张纸最显眼的地方。
 *
 * 这里走的是 **`<foreignObject>` 序列化**：把 DOM 原样塞进一张 SVG，
 * 交给**浏览器自己的渲染器**画到 canvas 上。渐变字、斜切、阴影全部天然正确，
 * 而且**一个依赖都不用加**（同 `modules/upload` 手写腾讯云签名 v5 的取舍）。
 *
 * ## ⚠ 这条路的三个前提，缺一张纸就白/破
 * 1. **不能有任何外部资源**——SVG 里的 `<img src="/api/img/…">` 浏览器**不会去取**（安全限制），
 *    所以图片必须先转成 `data:` URI（`inlineImages`）；
 * 2. **CSS 必须内联进 SVG**（`collectCss`）：foreignObject 里的元素读不到页面的样式表；
 * 3. **字体只能用系统字体**——快报刻意没有 webfont，正是为了这一步。
 *
 * ⚠ 导出的是**纸本身**，不带预览的缩放（`.bp-shot` 把 `--bp-k` 顶掉）。
 * ⚠ 页面上的提示（快照过时 / 照片缺失）**不进导出物**，它们本来就在纸外面。
 */

/** 导出倍率。2 倍在 860px 的纸上 ≈ 1720px 宽，微信里放大看仍然清楚；再高文件会很大 */
const SCALE = 2;
/** JPEG 质量（PDF 用）。0.92 是"看不出压缩痕迹"的常见档位 */
const JPEG_Q = 0.92;

/* ===================== 一、把外部资源变成 data: ===================== */

async function toDataUrl(url: string): Promise<string> {
  /* ⚠ `credentials: 'include'` 不能少：`/api/img/*` 是**登录后才给**的（后端挂了 requireLogin） */
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`取图失败 ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('图片编码失败'));
    fr.readAsDataURL(blob);
  });
}

/**
 * 把克隆体里所有 `<img>` 换成 data: URI。
 * ⚠ **取不到的图直接摘掉**，不让整次导出失败——与页面上「照片已被删除时该图位整个不占版面」
 *   是同一条口径（`16` §八）。底下垫着的占位格会露出来。
 */
async function inlineImages(root: HTMLElement): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src') ?? '';
      if (!src || src.startsWith('data:')) return;
      try {
        img.setAttribute('src', await toDataUrl(src));
      } catch {
        img.remove();
      }
    }),
  );
}

/**
 * 收集同源样式表的全部规则。
 * ⚠ 一股脑全收而不是只挑 `.bp` 开头的：挑选逻辑一旦漏掉一条（比如 `@media` 里的、
 *   或者 `.bl-ed` 这种跨段的），表现是**导出物少了一处样式**，而屏幕上一切正常——
 *   这种错最难发现。全量约 100KB，对一次导出完全不是问题。
 * ⚠ 跨域样式表读 `cssRules` 会抛 SecurityError，跳过即可（本项目没有外部样式表）。
 */
function collectCss(): string {
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const rule of Array.from(sheet.cssRules)) out.push(rule.cssText);
    } catch {
      /* 跨域样式表，跳过 */
    }
  }
  return out.join('\n');
}

/* ===================== 二、DOM → canvas ===================== */

/**
 * 把节点画成 canvas。
 * ⚠ `width` / `height` 取的是**布局尺寸**（`offsetWidth` / `offsetHeight`），
 *   不是 `getBoundingClientRect()`——后者带着预览的 `transform: scale()`，
 *   量出来会是缩放后的值，导出物会莫名其妙地小一圈。
 */
async function toCanvas(node: HTMLElement): Promise<HTMLCanvasElement> {
  const w = node.offsetWidth;
  const h = node.offsetHeight;

  const clone = node.cloneNode(true) as HTMLElement;
  await inlineImages(clone);

  const xml = new XMLSerializer().serializeToString(clone);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">` +
    `<foreignObject width="100%" height="100%">` +
    `<div xmlns="http://www.w3.org/1999/xhtml">` +
    `<style>${collectCss()}</style>${xml}` +
    `</div></foreignObject></svg>`;

  /* 🔴 **必须是 `data:` URI，不能用 `URL.createObjectURL`**（2026-08-25 真跑时踩到）。
     blob: 地址的 SVG 画进 canvas 会把画布**污染**（tainted），随后 `toBlob` 直接抛
     「Tainted canvases may not be exported」——**类型检查与单测都测不出来，只有真跑才暴露**。
     data: 被当作同源，不污染。⚠ 因此 `encodeURIComponent` 也不能省：CSS 里到处是 `#`，
     不编码会被当成片段标识符，SVG 会从 `#` 处被截断。 */
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('快报渲染失败，请重试'));
    el.src = url;
  });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * SCALE);
  canvas.height = Math.round(h * SCALE);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('浏览器不支持画布导出');
  /* ⚠ 先铺一层纸底色：PNG 默认透明，贴进聊天窗口 / 塞进 PDF 会变成黑底 */
  ctx.fillStyle = '#EDF3FC';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  ctx.drawImage(img, 0, 0);
  return canvas;
}

/* ===================== 三、下载 ===================== */

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  /* ⚠ 立刻 revoke 会让部分浏览器来不及取，留一拍再回收 */
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const canvasBlob = (canvas: HTMLCanvasElement, type: string, q?: number): Promise<Blob> =>
  new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('导出失败，请重试'))), type, q),
  );

/* ===================== 四、单页 PDF（手写，零依赖） ===================== */

/**
 * 把一张 JPEG 包成**单页 PDF**。
 *
 * ⚠ 为什么手写而不是引 jsPDF：我们只需要「一页 + 一张图」，PDF 的这一小块结构非常简单
 *   （五个对象 + 一张 xref 表），60 行就够；引一个 300KB 的库只用到它 1% 的能力，
 *   还要把它一起打进产物。
 * ⚠ **字不可选**（整页是一张图）——对「发出去给人看」的用途没有影响，`16` §七之二 已写明。
 * ⚠ 页宽固定 A4 宽度 595.28pt，页高按图片比例算：产物是**一张长条**，
 *   在阅读器里连续滚动看，与长图是同一个观感。
 */
function buildPdf(jpeg: Uint8Array, pxW: number, pxH: number): Blob {
  const PW = 595.28;
  const PH = (pxH / pxW) * PW;

  const parts: Uint8Array[] = [];
  const offsets: number[] = [];
  let len = 0;
  const enc = new TextEncoder();
  /* ⚠ PDF 的结构部分必须按**字节**计偏移，所以这里统一走 Uint8Array，不拼字符串 */
  const push = (u: Uint8Array): void => {
    parts.push(u);
    len += u.length;
  };
  const text = (s: string): void => push(enc.encode(s));
  const mark = (): void => {
    offsets.push(len);
  };

  text('%PDF-1.4\n');
  mark();
  text('1 0 obj\n<</Type/Catalog/Pages 2 0 R>>\nendobj\n');
  mark();
  text('2 0 obj\n<</Type/Pages/Kids[3 0 R]/Count 1>>\nendobj\n');
  mark();
  text(
    `3 0 obj\n<</Type/Page/Parent 2 0 R/MediaBox[0 0 ${PW.toFixed(2)} ${PH.toFixed(2)}]` +
      `/Resources<</XObject<</Im0 4 0 R>>>>/Contents 5 0 R>>\nendobj\n`,
  );
  mark();
  text(
    `4 0 obj\n<</Type/XObject/Subtype/Image/Width ${pxW}/Height ${pxH}` +
      `/ColorSpace/DeviceRGB/BitsPerComponent 8/Filter/DCTDecode/Length ${jpeg.length}>>\nstream\n`,
  );
  push(jpeg);
  text('\nendstream\nendobj\n');
  mark();
  const content = `q ${PW.toFixed(2)} 0 0 ${PH.toFixed(2)} 0 0 cm /Im0 Do Q\n`;
  text(`5 0 obj\n<</Length ${content.length}>>\nstream\n${content}endstream\nendobj\n`);

  const xrefAt = len;
  let xref = 'xref\n0 6\n0000000000 65535 f \n';
  for (const o of offsets) xref += `${String(o).padStart(10, '0')} 00000 n \n`;
  text(xref);
  text(`trailer\n<</Size 6/Root 1 0 R>>\nstartxref\n${xrefAt}\n%%EOF\n`);

  return new Blob(parts as BlobPart[], { type: 'application/pdf' });
}

/* ===================== 五、对外 ===================== */

export type ExportKind = 'png' | 'pdf';

/**
 * 导出快报。
 * @param node 纸的根元素（`.bp`）
 * @param baseName 文件名（不含扩展名）
 *
 * ⚠ 导出期间给祖先加 `.bp-shot`：预览是等比缩过的，**不能带着缩放去截图**。
 *   加在祖先上是因为缩放写在 `.bp-fit` 上，而 `node` 是它的子元素。
 */
export async function exportPaper(
  node: HTMLElement,
  baseName: string,
  kind: ExportKind,
): Promise<void> {
  const stage = node.closest('.bl-view') ?? document.body;
  stage.classList.add('bp-shot');
  try {
    const canvas = await toCanvas(node);
    if (kind === 'png') {
      download(await canvasBlob(canvas, 'image/png'), `${baseName}.png`);
      return;
    }
    const jpeg = await canvasBlob(canvas, 'image/jpeg', JPEG_Q);
    const bytes = new Uint8Array(await jpeg.arrayBuffer());
    download(buildPdf(bytes, canvas.width, canvas.height), `${baseName}.pdf`);
  } finally {
    stage.classList.remove('bp-shot');
  }
}
